import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Users, Search, CheckCircle2, Clock, AlertTriangle,
  MessageSquare, Send, Eye, Wrench, ChevronRight,
  Link, Copy, ExternalLink, Trash2
} from 'lucide-react';

const SEVERITY_COLORS = {
  blocker: 'bg-red-100 text-red-800', major: 'bg-orange-100 text-orange-800',
  minor: 'bg-yellow-100 text-yellow-800', observation: 'bg-slate-100 text-slate-600',
};

const STATUS_COLORS = {
  assigned: 'bg-blue-100 text-blue-700', acknowledged: 'bg-indigo-100 text-indigo-700',
  in_progress: 'bg-cyan-100 text-cyan-700', rectified_by_subcontractor: 'bg-teal-100 text-teal-700',
  awaiting_contractor_check: 'bg-amber-100 text-amber-700', closed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

export default function SubcontractorPortal({ projectId, settings }) {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDefect, setActiveDefect] = useState(null);
  const [showComment, setShowComment] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [showGenerate, setShowGenerate] = useState(false);
  const [generateCompany, setGenerateCompany] = useState('');
  const [generateEmail, setGenerateEmail] = useState('');
  const [generateSendEmail, setGenerateSendEmail] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [portalLinks, setPortalLinks] = useState([]);

  // Load companies from task allocation data AND subcontractor users
  useEffect(() => {
    (async () => {
      try {
        const [taskRes, linksRes, subsRes] = await Promise.all([
          api.closeout.getTaskAllocation(projectId, {}),
          api.closeout.portalLinks(projectId),
          api.get(`/diary/subcontractors/${projectId}`),
        ]);
        const taskCompanies = taskRes.data.companies || [];
        const subUsers = subsRes.data.subcontractors || [];
        const subCompanyNames = subUsers.map(s => s.company_name || s.name).filter(Boolean);
        // Merge: task allocation companies + subcontractor user companies (deduplicated)
        const merged = [...new Set([...taskCompanies, ...subCompanyNames])].sort();
        setCompanies(merged);
        setPortalLinks(linksRes.data || []);
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, [projectId]);

  const loadDefects = useCallback(async (company) => {
    if (!company) return;
    setLoading(true);
    try {
      const res = await api.closeout.portalMyDefects(company, projectId);
      setDefects(res.data || []);
    } catch { toast.error('Failed to load defects'); } finally { setLoading(false); }
  }, [projectId]);

  const selectCompany = (co) => {
    setSelectedCompany(co);
    loadDefects(co);
  };

  const handleAcknowledge = async (defectId) => {
    try {
      await api.closeout.portalAcknowledge(defectId);
      toast.success('Defect acknowledged');
      loadDefects(selectedCompany);
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot acknowledge'); }
  };

  const handleRectify = async (defectId) => {
    try {
      await api.closeout.portalRectify(defectId);
      toast.success('Marked as rectified');
      loadDefects(selectedCompany);
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot mark as rectified'); }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !activeDefect) return;
    try {
      await api.closeout.portalComment(activeDefect, commentText);
      toast.success('Comment added');
      setShowComment(false);
      setCommentText('');
    } catch { toast.error('Failed to add comment'); }
  };

  const handleGenerateLink = async () => {
    if (!generateCompany) { toast.error('Select a company'); return; }
    try {
      const res = await api.closeout.generatePortalLink({
        project_id: projectId,
        company_name: generateCompany,
        contact_email: generateEmail || null,
        expires_days: 30,
        send_email: generateSendEmail && !!generateEmail,
      });
      const fullUrl = `${window.location.origin}${res.data.url_path}`;
      setGeneratedLink(fullUrl);
      toast.success(generateSendEmail && generateEmail ? 'Portal link generated and emailed' : 'Portal link generated');
      // Refresh links list
      const linksRes = await api.closeout.portalLinks(projectId);
      setPortalLinks(linksRes.data || []);
    } catch { toast.error('Failed to generate link'); }
  };

  const handleCopyLink = (link) => {
    const fullUrl = `${window.location.origin}/closeout-portal/${link.token}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('Link copied to clipboard');
  };

  const handleRevokeLink = async (linkId) => {
    try {
      await api.closeout.revokePortalLink(linkId);
      toast.success('Link revoked');
      setPortalLinks(prev => prev.filter(l => l.id !== linkId));
    } catch { toast.error('Failed to revoke'); }
  };

  if (loading && !selectedCompany) {
    return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;
  }

  // Company selection view
  if (!selectedCompany) {
    return (
      <div className="space-y-4" data-testid="subcontractor-portal">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">Subcontractor Portal</h2>
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1" onClick={() => setShowGenerate(true)} data-testid="generate-portal-link-btn">
            <Link className="h-4 w-4" /> Generate External Link
          </Button>
        </div>
        <p className="text-sm text-slate-500">Select a subcontractor company to view their assigned defects, or generate a portal link for direct external access</p>

        {companies.length === 0 ? (
          <Card><CardContent className="p-12 text-center">
            <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-500">No subcontractors assigned yet</p>
            <p className="text-sm text-slate-400 mt-1">Use Task Allocation to assign defects to subcontractor companies</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {companies.map(co => {
              const hasLink = portalLinks.some(l => l.company_name === co);
              return (
                <Card key={co} className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-slate-300" onClick={() => selectCompany(co)} data-testid={`portal-company-${co}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-slate-900 text-white"><Users className="h-5 w-5" /></div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-slate-800">{co}</h3>
                        <p className="text-xs text-slate-500">{hasLink ? 'External link active' : 'View assigned defects'}</p>
                      </div>
                      {hasLink && <ExternalLink className="h-3.5 w-3.5 text-green-500" />}
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Active Portal Links */}
        {portalLinks.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Active Portal Links ({portalLinks.length})</h3>
              <div className="space-y-2">
                {portalLinks.map(link => (
                  <div key={link.id} className="flex items-center gap-3 p-2.5 rounded bg-slate-50 border" data-testid={`portal-link-${link.id}`}>
                    <ExternalLink className="h-4 w-4 text-green-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{link.company_name}</p>
                      <p className="text-xs text-slate-500">{link.contact_email || 'No email'} | Expires: {link.expires_at?.slice(0, 10)}</p>
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => handleCopyLink(link)} data-testid={`copy-link-${link.id}`}>
                      <Copy className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500 hover:text-red-700" onClick={() => handleRevokeLink(link.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Generate Link Dialog */}
        <Dialog open={showGenerate} onOpenChange={v => { setShowGenerate(v); if (!v) setGeneratedLink(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogTitle className="font-barlow font-bold uppercase">Generate Portal Link</DialogTitle>
            <DialogDescription>Create a shareable link for a subcontractor to access their defects, acknowledge items, and submit rectification confirmations — no login required.</DialogDescription>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs">Company *</Label>
                <select className="w-full h-9 border rounded-md px-3 text-sm bg-white" value={generateCompany} onChange={e => setGenerateCompany(e.target.value)} data-testid="generate-company-select">
                  <option value="">Select company...</option>
                  {companies.map(co => <option key={co} value={co}>{co}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Contact Email (optional)</Label>
                <Input value={generateEmail} onChange={e => setGenerateEmail(e.target.value)} placeholder="subcontractor@company.com" data-testid="generate-email-input" />
              </div>
              {generateEmail && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={generateSendEmail} onChange={e => setGenerateSendEmail(e.target.checked)} className="rounded" />
                  Send portal invitation email
                </label>
              )}
              {generatedLink && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-xs font-bold text-green-700 mb-1">Link Generated!</p>
                  <div className="flex items-center gap-2">
                    <Input value={generatedLink} readOnly className="text-xs bg-white" data-testid="generated-link-input" />
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(generatedLink); toast.success('Copied!'); }} data-testid="copy-generated-link">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowGenerate(false); setGeneratedLink(null); }}>Close</Button>
              {!generatedLink && (
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleGenerateLink} data-testid="generate-link-submit">
                  <Link className="h-4 w-4 mr-1" /> Generate Link
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Portal view for selected company
  const stats = {
    total: defects.length,
    pending_ack: defects.filter(d => d.status === 'assigned').length,
    in_progress: defects.filter(d => ['acknowledged', 'in_progress'].includes(d.status)).length,
    rectified: defects.filter(d => d.status === 'rectified_by_subcontractor').length,
    closed: defects.filter(d => d.status === 'closed').length,
    rejected: defects.filter(d => d.status === 'rejected').length,
  };

  return (
    <div className="space-y-4" data-testid="subcontractor-portal">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => { setSelectedCompany(''); setDefects([]); }} className="gap-1">
          <ChevronRight className="h-4 w-4 rotate-180" /> All Companies
        </Button>
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">{selectedCompany}</h2>
        <Badge className="bg-slate-900 text-white text-xs">{stats.total} items</Badge>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-2">
        <Card><CardContent className="p-2.5 text-center"><p className="text-xl font-bold text-blue-600">{stats.pending_ack}</p><p className="text-[9px] text-slate-500 uppercase">Awaiting Ack</p></CardContent></Card>
        <Card><CardContent className="p-2.5 text-center"><p className="text-xl font-bold text-cyan-600">{stats.in_progress}</p><p className="text-[9px] text-slate-500 uppercase">In Progress</p></CardContent></Card>
        <Card><CardContent className="p-2.5 text-center"><p className="text-xl font-bold text-teal-600">{stats.rectified}</p><p className="text-[9px] text-slate-500 uppercase">Rectified</p></CardContent></Card>
        <Card><CardContent className="p-2.5 text-center"><p className="text-xl font-bold text-green-600">{stats.closed}</p><p className="text-[9px] text-slate-500 uppercase">Closed</p></CardContent></Card>
        <Card><CardContent className="p-2.5 text-center"><p className="text-xl font-bold text-red-600">{stats.rejected}</p><p className="text-[9px] text-slate-500 uppercase">Rejected</p></CardContent></Card>
      </div>

      {/* Defect List */}
      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>
      ) : defects.length === 0 ? (
        <Card><CardContent className="p-12 text-center"><Wrench className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-500">No assigned defects for this company</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {defects.map(d => (
            <Card key={d.id} className="hover:shadow-sm transition-shadow" data-testid={`portal-defect-${d.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-slate-400">{d.defect_no}</span>
                      <Badge className={`text-[10px] ${SEVERITY_COLORS[d.severity]}`}>{d.severity}</Badge>
                      <Badge className={`text-[10px] ${STATUS_COLORS[d.status] || 'bg-slate-100'}`}>{d.status?.replace(/_/g, ' ')}</Badge>
                      {d.completion_blocker_flag && <Badge className="bg-red-50 text-red-600 text-[9px]">Blocker</Badge>}
                    </div>
                    <p className="text-sm font-medium text-slate-800">{d.description_short}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      {d.location_text && <span>{d.location_text}</span>}
                      {d.trade_code && <span>{d.trade_code.replace(/_/g, ' ')}</span>}
                      {d.target_rectification_date && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Due: {d.target_rectification_date}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {d.status === 'assigned' && (
                      <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 gap-1" onClick={() => handleAcknowledge(d.id)} data-testid={`ack-btn-${d.id}`}>
                        <CheckCircle2 className="h-3 w-3" /> Acknowledge
                      </Button>
                    )}
                    {['acknowledged', 'in_progress', 'rejected'].includes(d.status) && (
                      <Button size="sm" className="h-7 text-xs bg-teal-600 hover:bg-teal-700 gap-1" onClick={() => handleRectify(d.id)} data-testid={`rectify-btn-${d.id}`}>
                        <Wrench className="h-3 w-3" /> Mark Rectified
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => { setActiveDefect(d.id); setShowComment(true); }}>
                      <MessageSquare className="h-3 w-3" /> Comment
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Comment Dialog */}
      <Dialog open={showComment} onOpenChange={setShowComment}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="font-barlow font-bold uppercase">Add Comment</DialogTitle>
          <DialogDescription>This comment will be visible to the project team</DialogDescription>
          <Textarea value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Type your comment..." rows={4} data-testid="portal-comment-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComment(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleAddComment} data-testid="portal-comment-submit">
              <Send className="h-4 w-4 mr-1" /> Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
